import { useMemo, useState } from 'react';
import { Button, Card, Popconfirm, Segmented, Space, Tag as AntTag, Tooltip } from 'antd';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useDebounce } from '@shared/hooks/useDebounce';
import { DataTable } from '@shared/components/DataTable';
import { PageHeader } from '@shared/components/PageHeader/PageHeader';
import { tagsApi } from '@api/tags';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { recordAuditAction } from '@features/audit/recordAction';
import { TagDrawer } from '../components/TagDrawer';
import type { Tag, TagListQuery } from '../types';

type SortKey = NonNullable<TagListQuery['sort']>;

export function TagsPage() {
  const { t } = useTranslation(['tags', 'common', 'validation', 'errors']);
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [sort, setSort] = useState<SortKey>('name');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<Tag | null>(null);

  const { data, isFetching } = useQuery({
    queryKey: ['tags', { page, pageSize, q: debouncedSearch, sort }],
    queryFn: () => tagsApi.list({ page, pageSize, q: debouncedSearch, sort }),
    placeholderData: (prev) => prev,
  });

  const deleteMutation = useMutation({
    mutationFn: (t: Tag) => tagsApi.remove(t.id),
    onSuccess: async (_d, tag) => {
      toast.success(t('toast.deleted', { name: tag.name }));
      await qc.invalidateQueries({ queryKey: ['tags'] });
      void recordAuditAction({
        action: 'tag.delete',
        resource: 'tag',
        resourceId: tag.id,
        summary: t('audit.delete', { name: tag.name }),
      });
    },
    onError: (err) => {
      toast.error(resolveApiErrorMessage(err));
    },
  });

  const columns = useMemo(
    () => [
      {
        key: 'tag',
        title: t('table.columns.tag'),
        dataIndex: 'name',
        render: (_: unknown, row: Tag) => (
          <AntTag color={row.color} style={{ margin: 0, fontSize: 13, padding: '2px 10px' }}>
            {row.name}
          </AntTag>
        ),
      },
      {
        key: 'slug',
        title: t('table.columns.slug'),
        dataIndex: 'slug',
        responsive: ['sm' as const],
        render: (v: string) => (
          <code style={{ fontSize: 12, color: 'var(--ds-text-secondary)' }}>{v}</code>
        ),
      },
      {
        key: 'description',
        title: t('common:labels.description'),
        dataIndex: 'description',
        responsive: ['md' as const],
        render: (v?: string) =>
          v ?? <span style={{ color: 'var(--ds-text-tertiary)', fontSize: 12 }}>—</span>,
      },
      {
        key: 'usage',
        title: t('table.columns.usage'),
        dataIndex: 'usageCount',
        width: 110,
        align: 'right' as const,
        responsive: ['lg' as const],
        render: (n: number) => (
          <span
            style={{
              fontVariantNumeric: 'tabular-nums',
              fontWeight: n > 0 ? 600 : 400,
              color: n > 0 ? undefined : 'var(--ds-text-tertiary)',
            }}
          >
            {n}
          </span>
        ),
      },
      {
        key: 'actions',
        title: '',
        width: 110,
        render: (_: unknown, row: Tag) => (
          <Space>
            <Tooltip title={t('tooltip.edit')}>
              <Button
                size="small"
                icon={<Pencil size={14} />}
                onClick={() => {
                  setEditing(row);
                  setDrawerOpen(true);
                }}
              />
            </Tooltip>
            <Popconfirm
              title={t('confirm.deleteTitle')}
              description={
                row.usageCount > 0
                  ? t('confirm.deleteInUse', { name: row.name, count: row.usageCount })
                  : t('confirm.deleteDefault', { name: row.name })
              }
              okText={t('common:actions.delete')}
              okButtonProps={{ danger: true }}
              cancelText={t('common:actions.cancel')}
              onConfirm={() => deleteMutation.mutate(row)}
            >
              <Tooltip title={t('tooltip.delete')}>
                <Button size="small" danger icon={<Trash2 size={14} />} />
              </Tooltip>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [deleteMutation, t],
  );

  return (
    <div>
      <PageHeader
        title={t('page.title')}
        description={t('page.description')}
        breadcrumb={[
          { label: t('common:labels.home'), to: '/' },
          { label: t('breadcrumb.taxonomy') },
          { label: t('breadcrumb.list') },
        ]}
        actions={
          <Button
            type="primary"
            icon={<Plus size={14} />}
            onClick={() => {
              setEditing(null);
              setDrawerOpen(true);
            }}
          >
            {t('page.create')}
          </Button>
        }
      />

      <Card>
        <DataTable<Tag>
          data={data?.items ?? []}
          columns={columns}
          rowKey="id"
          loading={isFetching}
          search={{
            value: search,
            onChange: (v) => {
              setSearch(v);
              setPage(1);
            },
            placeholder: t('table.searchPlaceholder'),
          }}
          filters={
            <Segmented<SortKey>
              size="small"
              value={sort}
              onChange={(v) => {
                setSort(v);
                setPage(1);
              }}
              options={[
                { value: 'name', label: t('sort.name') },
                { value: 'usage', label: t('sort.usage') },
                { value: 'recent', label: t('sort.recent') },
              ]}
            />
          }
          pagination={{
            page,
            size: pageSize,
            total: data?.total ?? 0,
            onChange: (p, s) => {
              setPage(p);
              setPageSize(s);
            },
          }}
          exportable
          exportFileName="tags"
        />
      </Card>

      <TagDrawer
        open={drawerOpen}
        tag={editing}
        onClose={() => {
          setDrawerOpen(false);
          setEditing(null);
        }}
      />
    </div>
  );
}
